import './TodayChat.scss'

import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Buffer } from 'buffer'
import { Button, Collapse, Form, InputGroup } from 'react-bootstrap'

import { getAuthenticatedUser } from 'components/auth'

import RembleAvatar from '../../../assets/images/Remble_Logomark-Green-25pGreen.png'
import {
  KEY_CHAT_TYPE,
  ROUTE_CHAT,
  ROUTE_CHAT_GENERAL,
} from 'components/common/helpers/Paths'
import { navigateTo } from 'components/common/navigation'
import { buildRequestURL } from 'components/common/api'
import useUserHasAcceptedAITOS from 'components/common/hooks/useUserHasAcceptedTOS'

/**
 * Today Chat component. Used for displaying the chat intro on the Today screen.
 */
export default function TodayChat({ userPreferences }) {
  const history = useHistory()
  const user = getAuthenticatedUser()
  const { loading: termsLoading, accepted: termsAccepted } =
    useUserHasAcceptedAITOS()

  const [message, setMessage] = useState('')
  const [open, setOpen] = useState(false)

  // Handle submit
  const handleSubmit = () => {
    // If we don't have a message, return
    if (!message) return

    // Base64 encode the message
    const encodedMessage = Buffer.from(message).toString('base64')

    // Build our request URL
    const uri = ROUTE_CHAT.replace(`:${KEY_CHAT_TYPE}`, 'open')
    const url = buildRequestURL(uri, { message: encodedMessage })

    navigateTo(url, history)
  }

  // Return early if we haven't loaded the terms yet
  if (termsLoading) return null

  return (
    <div className='today-chat' id='js-tour-today-title'>
      <h2 className='today-title'>Chat with Mia</h2>
      <div className='chat-messages'>
        <div className='chat-message'>
          <div className='chat-message-avatar assistant'>
            <img src={RembleAvatar} alt='' width='100%' />
          </div>

          {termsAccepted ? (
            <div className='chat-message-content'>
              <p>
                How can I help you today
                {user?.display_name && `, ${user.display_name}`}?
              </p>
            </div>
          ) : (
            <>
              <div className='chat-message-content'>
                <p>
                  {/* If the user hasn't accepted the AI Usage terms, show a different message */}
                  Hello{user?.display_name && `, ${user.display_name}`}! Before
                  we chat, I want to make sure that you understand something
                  important about our conversation. While I aim to provide
                  support and understanding as best I can, I am not a human, but
                  an artificial intelligence (AI) assistant. However, this
                  doesn't mean our conversations will be any less meaningful or
                  beneficial. I'm designed to interact smoothly and competently,
                  much like a human would.
                </p>
              </div>
              {!open && (
                <div
                  className='expandable-text muted my-2 text-right'
                  onClick={_ => setOpen(!open)}
                >
                  tell me more
                </div>
              )}
              <Collapse in={open}>
                <div className='chat-message-content my-2 expanded-text'>
                  <p>
                    I'm happy to tell you more. My programming is grounded in a
                    wide range of expert knowledge in mental health, and I can
                    offer strategies, tips, and guidance based on this. While as
                    an AI, I don't experience emotions in the human sense, I can
                    be an effective partner in your mental health journey.
                    Remember, I am here to provide a nonjudgmental and safe
                    space for you to express yourself. I'm available to chat
                    24/7, and my primary goal is to make our conversations as
                    easy and comfortable for you as possible. Feel free to share
                    as much or as little as you wish, and we'll navigate this
                    journey together.
                  </p>
                </div>
              </Collapse>
            </>
          )}
        </div>
      </div>

      <InputGroup className='chat-message-input-group'>
        <Form.Control
          className='message-input'
          name='message'
          id='message-field'
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={e => {
            // Search on enter, but only if the user is NOT holding down shift, we aren't loading, and the query is long enough
            if (e.key === 'Enter') handleSubmit()
          }}
          placeholder='Send a message'
          autoComplete='off'
        />

        <Button
          className='chat-message-input-group'
          onClick={_ => handleSubmit()}
          title='Send Message'
        >
          <i className='bi bi-send'></i>
        </Button>
      </InputGroup>
      <div className='text-right muted small my-3'>
        <p>
          <small>
            <span
              className='green text-decoration-underline cursor-pointer'
              onClick={() => navigateTo(ROUTE_CHAT_GENERAL, history)}
            >
              See how I can help
            </span>
          </small>
        </p>
      </div>
    </div>
  )
}
